// Verify một remote URL có accessible không bằng `git ls-remote <url> HEAD`
// với timeout 5 giây. Tách ra để init flow check sớm — fail-fast nếu URL sai.
//
// v1.2.4: Capture stderr để caller diagnose root cause (private no access
// vs URL wrong vs network). Thêm `tryVerifyGitRemoteAccessible` non-throwing
// variant trả structured result cho UX recovery flow.
import { spawnSync } from "node:child_process";
import { assertAllowedGitUrlScheme } from "./validate-git-url-scheme-allowlist.js";

const TIMEOUT_MS = 5_000;

export type RemoteFailReason =
  | "timeout"
  | "no-access" // 403/auth required (private repo + wrong user)
  | "not-found" // 404 URL wrong hoặc repo bị xóa
  | "network" // DNS / unreachable
  | "invalid-scheme" // URL dùng git transport bị chặn (ext::, file://, ...) — VULN-4a
  | "unknown";

export interface RemoteAccessResult {
  ok: boolean;
  reason?: RemoteFailReason;
  detail?: string; // stderr sample để log debug
}

export class RemoteNotAccessibleError extends Error {
  reason: RemoteFailReason;
  detail?: string;
  url: string;
  constructor(url: string, reason: RemoteFailReason, detail?: string) {
    super(`Không truy cập được remote ${url}: ${reason}${detail ? ` (${detail})` : ""}`);
    this.name = "RemoteNotAccessibleError";
    this.reason = reason;
    this.detail = detail;
    this.url = url;
  }
}

// Classify git ls-remote stderr thành reason code chuẩn hóa.
// Patterns dựa theo git output thực tế trên GitHub HTTPS auth-required scenarios.
function classifyRemoteError(stderr: string): RemoteFailReason {
  const text = stderr.toLowerCase();
  if (
    text.includes("authentication") ||
    text.includes("could not read username") ||
    text.includes("terminal prompts disabled") || // git với GIT_TERMINAL_PROMPT=0 trả message này
    text.includes("permission denied") ||
    text.includes("403") ||
    text.includes("access denied") ||
    text.includes("repository not found") // GitHub trả "not found" cho cả private no-access (bảo mật)
  ) {
    return "no-access";
  }
  if (text.includes("404") || text.includes("does not exist")) {
    return "not-found";
  }
  if (
    text.includes("could not resolve host") ||
    text.includes("network") ||
    text.includes("connection refused") ||
    text.includes("connection timed out")
  ) {
    return "network";
  }
  return "unknown";
}

// Non-throwing variant — trả structured result cho caller decide flow.
// Recommend dùng cho flow cần fallback recovery (vd account switch).
//
// v1.13.0 fix: Check `r.error` TRƯỚC khi classify. spawnSync trả error khi:
//   - Binary not found (ENOENT) — git chưa cài
//   - Permission denied (EACCES)
//   - Killed by signal khác SIGTERM
// Trước fix: r.status=null + stderr="" → classify("") → "unknown" → user thấy
// "Lỗi không xác định" thay vì "git chưa cài". Pattern này đã chuẩn ở
// check-claude-code-subscription-and-quota.ts:38, sync hóa cho consistency.
export function tryVerifyGitRemoteAccessible(url: string): RemoteAccessResult {
  // VULN-4a: chặn scheme lạ (ext::, file://) TRƯỚC khi truyền vào spawnSync —
  // git's `ext::` transport cho phép chạy shell tùy ý.
  //
  // H2 fix: hàm này là "non-throwing variant" (tên + docblock ở trên cam kết
  // trả structured result, KHÔNG throw) nhưng assertAllowedGitUrlScheme throw
  // raw Error → 3 production caller (git-auth-and-install-orchestrator.ts,
  // handle-remote-access-failure-with-account-switch.ts, clone-repo-with-
  // recovery-menu.ts) gọi hàm này KHÔNG có try/catch bọc ngoài → crash thay vì
  // rơi vào recovery-menu UX. Bọc lại thành structured fail, KHÔNG để throw
  // thoát khỏi hàm non-throwing.
  try {
    assertAllowedGitUrlScheme(url);
  } catch (err) {
    return {
      ok: false,
      reason: "invalid-scheme",
      detail: (err as Error).message.slice(0, 300),
    };
  }
  const r = spawnSync("git", ["ls-remote", "--exit-code", url, "HEAD"], {
    encoding: "utf8",
    timeout: TIMEOUT_MS,
    stdio: ["ignore", "pipe", "pipe"],
    // Fix HANG: GIT_TERMINAL_PROMPT=0 ngăn git mở /dev/tty hỏi username/password
    // khi không có cached credential. Không có env này → git block stdin dù đã
    // set stdio:["ignore",...], nuốt keystrokes, TTY bị broken sau timeout SIGTERM.
    env: { ...process.env, GIT_TERMINAL_PROMPT: "0" },
  });
  if (r.error) {
    const err = r.error as NodeJS.ErrnoException;
    if (err.code === "ENOENT") {
      return {
        ok: false,
        reason: "network",
        detail: "git binary không tìm thấy — cài git rồi retry",
      };
    }
    if (err.code === "ETIMEDOUT") {
      return { ok: false, reason: "timeout", detail: `git ls-remote > ${TIMEOUT_MS / 1000}s` };
    }
    return { ok: false, reason: "unknown", detail: err.message.slice(0, 300) };
  }
  if (r.status === 0) return { ok: true };
  if (r.signal === "SIGTERM") {
    return { ok: false, reason: "timeout", detail: `git ls-remote > ${TIMEOUT_MS / 1000}s` };
  }
  const stderr = (r.stderr || "").trim();
  const reason = classifyRemoteError(stderr);
  return { ok: false, reason, detail: stderr.slice(0, 300) };
}

// Throwing variant — backward compat cho existing callers.
// Caller catch RemoteNotAccessibleError nếu cần handle.
export function verifyGitRemoteAccessible(url: string): void {
  const result = tryVerifyGitRemoteAccessible(url);
  if (result.ok) return;
  throw new RemoteNotAccessibleError(url, result.reason ?? "unknown", result.detail);
}
